import React, { useState } from 'react';
import axios from 'axios';
import { TESTIDS } from '@/lib/testIds';
import { School, BookOpen, CalendarHeart, Mic2, Newspaper, Mail } from 'lucide-react';
import { Gladiolus, Sprig } from './Illustrations';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inquiryOptions = [
  { value: 'school_visit', label: 'School Visit', icon: School },
  { value: 'library_visit', label: 'Library Visit', icon: BookOpen },
  { value: 'author_event', label: 'Author Event', icon: CalendarHeart },
  { value: 'speaking', label: 'Speaking Engagement', icon: Mic2 },
  { value: 'media', label: 'Media Inquiry', icon: Newspaper },
  { value: 'general', label: 'General Contact', icon: Mail },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    inquiry_type: 'general',
    message: '',
  });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setError('');
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });
      setStatus('success');
      setForm({ name: '', email: '', inquiry_type: 'general', message: '' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Please check the form and try again.';
      setError(msg);
      setStatus('error');
    }
  };

  return (
    <section
      id="contact"
      data-testid={TESTIDS.contact.section}
      className="relative py-24 md:py-32 bg-[#FBE8EE]/50 overflow-hidden"
    >
      <Gladiolus className="absolute -left-10 top-16 w-40 opacity-70 hidden md:block" />
      <Sprig className="absolute right-6 bottom-8 w-48 opacity-60" />

      <div className="max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-start">
        {/* Left info */}
        <div>
          <p className="font-accent text-2xl text-[#8D68B2]">Say hello</p>
          <h2 className="font-heading text-4xl md:text-5xl text-[#24385C] mt-2 leading-tight">
            Let&rsquo;s grow <span className="squiggle-underline">something</span> beautiful together.
          </h2>
          <p className="mt-6 font-body text-[17px] text-[#24385C]/80 leading-relaxed max-w-md">
            Erica loves connecting with schools, libraries, and communities. Whether you&rsquo;re
            planning a visit, an event, or simply want to share how a story touched your family,
            your note is always welcome here.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {inquiryOptions.map(({ value, label, icon: Icon }) => (
              <div
                key={value}
                className="flex items-center gap-3 bg-white/80 rounded-2xl p-4 border-2 border-white shadow-sm"
              >
                <div className="w-10 h-10 rounded-full bg-[#B88ACB]/20 flex items-center justify-center">
                  <Icon size={18} className="text-[#8D68B2]" strokeWidth={1.8} />
                </div>
                <span className="font-body font-semibold text-[#24385C] text-sm">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right form */}
        <div className="bg-white rounded-[32px] p-8 md:p-10 border-2 border-white shadow-[0_20px_50px_-20px_rgba(36,56,92,0.2)]">
          {status === 'success' ? (
            <div data-testid={TESTIDS.contact.success} className="text-center py-8">
              <p className="font-accent text-4xl text-[#8D68B2]">Thank you!</p>
              <p className="mt-3 font-body text-[#24385C]/80">
                Your note has landed safely. Erica will get back to you soon.
              </p>
              <button
                onClick={() => setStatus('idle')}
                className="mt-6 bg-[#F5C857] hover:bg-[#F1BE3E] text-[#24385C] rounded-full px-6 py-3 font-body font-semibold"
              >
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="font-body text-sm font-semibold text-[#24385C]">Your name</label>
                <input
                  data-testid={TESTIDS.contact.nameInput}
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full bg-[#FBF7F2] border-2 border-[#FBF7F2] focus:border-[#8D68B2] rounded-2xl px-5 py-3 font-body text-[#24385C] outline-none transition-all"
                  placeholder="Jane Reader"
                />
              </div>
              <div>
                <label className="font-body text-sm font-semibold text-[#24385C]">Email</label>
                <input
                  data-testid={TESTIDS.contact.emailInput}
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full bg-[#FBF7F2] border-2 border-[#FBF7F2] focus:border-[#8D68B2] rounded-2xl px-5 py-3 font-body text-[#24385C] outline-none transition-all"
                  placeholder="jane@school.org"
                />
              </div>
              <div>
                <label className="font-body text-sm font-semibold text-[#24385C]">What can we help with?</label>
                <select
                  data-testid={TESTIDS.contact.typeSelect}
                  value={form.inquiry_type}
                  onChange={(e) => setForm({ ...form, inquiry_type: e.target.value })}
                  className="mt-1 w-full bg-[#FBF7F2] border-2 border-[#FBF7F2] focus:border-[#8D68B2] rounded-2xl px-5 py-3 font-body text-[#24385C] outline-none transition-all"
                >
                  {inquiryOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-body text-sm font-semibold text-[#24385C]">Your message</label>
                <textarea
                  data-testid={TESTIDS.contact.messageInput}
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-1 w-full bg-[#FBF7F2] border-2 border-[#FBF7F2] focus:border-[#8D68B2] rounded-2xl px-5 py-3 font-body text-[#24385C] outline-none transition-all resize-none"
                  placeholder="Tell Erica a little about what you have in mind…"
                />
              </div>

              {status === 'error' && (
                <p className="font-body text-sm text-[#8D68B2]">{error}</p>
              )}

              <button
                data-testid={TESTIDS.contact.submitBtn}
                type="submit"
                disabled={status === 'loading'}
                className="w-full bg-[#8D68B2] hover:bg-[#24385C] disabled:opacity-60 text-white rounded-full px-6 py-4 font-body font-semibold shadow-md hover:shadow-lg transition-all"
              >
                {status === 'loading' ? 'Sending…' : 'Send message'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
